import * as React from 'react'
import { FileUp, Loader2, Upload, X } from 'lucide-react'
import {
  branchesApi,
  useAppDispatch,
  useImportNeo4jBackupMutation,
  useSwitchBranch
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import { ConfirmDialog } from '../../ConfigurePage/components/DeleteConfirmDialog'

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  if (error && typeof error === 'object') {
    const obj = error as Record<string, unknown>
    if (obj['data'] && typeof obj['data'] === 'object') {
      const data = obj['data'] as Record<string, unknown>
      if (typeof data['message'] === 'string') return data['message']
      if (typeof data['error'] === 'string') return data['error']
    }
    if (typeof obj['message'] === 'string') return obj['message']
    if (typeof obj['error'] === 'string') return obj['error']
  }
  return 'Unknown error.'
}

export function Neo4jBackupImportCard({ embedded = false }: { embedded?: boolean }) {
  const dispatch = useAppDispatch()
  const [importNeo4jBackup, importState] = useImportNeo4jBackupMutation()
  const { switchBranch, isOpening } = useSwitchBranch()

  const [name, setName] = React.useState('')
  const [file, setFile] = React.useState<File | null>(null)
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [error, setError] = React.useState('')
  const [createdName, setCreatedName] = React.useState('')
  const [fileInputKey, setFileInputKey] = React.useState(0)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const trimmed = name.trim()
  const busy = importState.isLoading || isOpening
  const canSubmit = trimmed.length > 0 && file != null && !busy

  const clearFile = React.useCallback(() => {
    setFile(null)
    setFileInputKey((key) => key + 1)
    setError('')
  }, [])

  const runImport = React.useCallback(async () => {
    if (!file || !trimmed) return
    setError('')
    setCreatedName('')
    try {
      const created = await importNeo4jBackup({ name: trimmed, file }).unwrap()
      dispatch(branchesApi.util.invalidateTags(['Branches', 'Commits']))
      await switchBranch(created.id)
      setCreatedName(created.name)
      setName('')
      clearFile()
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setConfirmOpen(false)
    }
  }, [clearFile, dispatch, file, importNeo4jBackup, switchBranch, trimmed])

  const body = (
    <div className="flex flex-col gap-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-ink-2">Import Neo4j backup</div>
          <p className="m-0 text-11 text-ink-3">
            <span className="font-mono">.backup</span> → committed branch off{' '}
            <span className="font-mono">main</span>
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          disabled={!canSubmit}
          onClick={() => {
            setError('')
            setConfirmOpen(true)
          }}
        >
          {busy ? <Loader2 className="animate-spin" /> : <Upload />}
          {busy ? 'Importing…' : 'Import'}
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Input
          aria-label="New branch name"
          placeholder="branch-name"
          value={name}
          onChange={(event) => {
            setName(event.target.value)
            setError('')
          }}
          className="h-8 min-w-0 flex-1 font-mono text-xs"
          disabled={busy}
        />

        <input
          key={fileInputKey}
          ref={fileInputRef}
          aria-label="Neo4j backup file"
          type="file"
          accept=".backup,application/octet-stream"
          className="sr-only"
          disabled={busy}
          onChange={(event) => {
            setFile(event.target.files?.[0] ?? null)
            setError('')
          }}
        />

        {file ? (
          <div className="flex h-8 min-w-0 max-w-[14rem] items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2">
            <FileUp className="size-3.5 shrink-0 text-ink-3" aria-hidden="true" />
            <span className="truncate font-mono text-11 text-ink-2" title={file.name}>
              {file.name}
            </span>
            <button
              type="button"
              aria-label="Clear backup file"
              className="shrink-0 rounded p-0.5 text-ink-3 hover:bg-muted hover:text-ink-2 disabled:opacity-50"
              disabled={busy}
              onClick={clearFile}
            >
              <X className="size-3.5" />
            </button>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 shrink-0"
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
          >
            <FileUp />
            Choose .backup
          </Button>
        )}
      </div>

      {error ? (
        <div role="alert" className="rounded-md bg-st-down-bg px-2 py-1 text-11 text-st-down">
          {error}
        </div>
      ) : null}

      {createdName ? (
        <p className="m-0 text-11 text-ink-2">
          Imported into <span className="font-mono">{createdName}</span> and switched to it.
        </p>
      ) : null}
    </div>
  )

  const dialog = (
    <ConfirmDialog
      open={confirmOpen}
      onOpenChange={setConfirmOpen}
      title="Import Neo4j backup?"
      description={`Creates branch "${trimmed}" off main, seeds its working database, and records the imported graph as a commit. Main and other branches are unchanged.`}
      confirmLabel="Import backup"
      busy={busy}
      onConfirm={() => void runImport()}
    />
  )

  if (embedded) {
    return (
      <>
        <div className="rounded-xl border bg-card px-4 py-3 shadow-sm">{body}</div>
        {dialog}
      </>
    )
  }

  return (
    <Card className="gap-0 py-0 shadow-sm">
      <CardContent className="px-4 py-2.5">{body}</CardContent>
      {dialog}
    </Card>
  )
}
